import type { App, Plugin as VuePlugin, InjectionKey } from 'vue';
import type { Router } from 'vue-router';
import { datadogRum, type Site } from '@datadog/browser-rum';
import { datadogLogs, type Logger } from '@datadog/browser-logs';

/**
 * 
 * Usage in a Vue component:
    import { ddLoggerKey } from "@/plugins/datadog";

    const ddLogger = inject(ddLoggerKey);
    ddLogger?.info?.("HomeView loaded", { "custom.message": "test" });
 */

const DD_VERSION = '1.0.0';

export interface DatadogOptions {
  // Required
  applicationId: string | undefined;
  clientToken: string | undefined;
  site: Site;
  service: string;
  env: string;

  // Optional, with sensible defaults
  sessionSampleRate?: number; // default 0
  sessionReplaySampleRate?: number; // default 0
  trackUserInteractions?: boolean; // default true
  trackResources?: boolean; // default true
  trackLongTasks?: boolean; // default true
  defaultPrivacyLevel?: 'mask-user-input' | 'mask' | 'allow'; // default 'mask-user-input'
  enableSessionReplay?: boolean; // default true
  forwardErrorsToLogs?: boolean; // default true
  forwardConsoleLogs?: Array<'debug' | 'info' | 'warn' | 'error'>; // default ['error', 'info', 'warn']
  allowedTracingUrls?: (string | RegExp)[]; // optional

  // Optional integrations
  router?: Router;

  // Optional kill switch function (mirrors your isDatadogLoggerEnabled)
  isEnabled?: () => boolean;
}

export interface DatadogPluginApi {
  ddLogger: Logger;
}

export const ddLoggerKey: InjectionKey<Logger> = Symbol('ddLogger');

/**
 * Creates a Vue plugin to initialize Datadog RUM + Logs.
 */
export function createDatadogPlugin(opts: DatadogOptions): VuePlugin {
  return {
    install(app: App) {
      // Allow a consumer-provided kill switch, and ensure we’re in the browser.
      if (typeof window === 'undefined') {
        return;
      }
      if (opts.isEnabled && !opts.isEnabled()) {
        return;
      }

      const {
        applicationId = '',
        clientToken = '',
        site,
        env,
        service,
        sessionSampleRate = 0,
        sessionReplaySampleRate = 0,
        trackUserInteractions = true,
        trackResources = true,
        trackLongTasks = true,
        defaultPrivacyLevel = 'mask-user-input',
        enableSessionReplay = true,
        forwardErrorsToLogs = true,
        forwardConsoleLogs = ['error', 'info', 'warn'],
        allowedTracingUrls,
        router,
      } = opts;

      // --- RUM ---
      datadogRum.init({
        applicationId,
        clientToken,
        site,
        service,
        env,
        version: DD_VERSION,

        // Core options
        sessionSampleRate: Number(sessionSampleRate),
        sessionReplaySampleRate: Number(sessionReplaySampleRate),
        trackUserInteractions,
        trackResources,
        trackLongTasks,
        defaultPrivacyLevel,

        // Uncomment if you need experimental features like flags + ConfigCat
        // enableExperimentalFeatures: ["feature_flags"],

        // If you also use Datadog APM for backends and want trace correlation:
        allowedTracingUrls: allowedTracingUrls ? allowedTracingUrls : [],
      });

      if (enableSessionReplay && sessionReplaySampleRate > 0) {
        datadogRum.startSessionReplayRecording();
      }

      // --- LOGS ---
      datadogLogs.init({
        clientToken,
        site,
        service,
        env,
        forwardErrorsToLogs,
        sessionSampleRate: Number(sessionSampleRate),
        version: DD_VERSION,
        forwardConsoleLogs,
        allowedTrackingOrigins: allowedTracingUrls,
      });

      // --- SPA routing instrumentation ---
      if (router) {
        router.afterEach((to) => {
          datadogRum.addAction('Route Change', {
            name: to.name ?? undefined,
            path: to.fullPath,
          });
        });
      }

      // Expose the logger:
      app.provide(ddLoggerKey, datadogLogs.logger);
    },
  };
}
